import React, { useCallback, useState, memo } from 'react';
import { Checkbox, Skeleton, Accordion, AccordionItem } from '@heroui/react';
import { ChevronRight, ChevronDown, FolderTree } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCategoryTree, CategoryTreeNode } from '../../../hooks/queries/useCategories';

// Get margin class based on level
const getMarginClass = (level: number): string => {
  const margins: Record<number, string> = {
    0: '',
    1: 'ml-4',
    2: 'ml-8',
    3: 'ml-12',
  };
  return margins[level] || 'ml-12';
};

// Helper functions for category operations
const getAllDescendantIds = (category: CategoryTreeNode): string[] => {
  const ids: string[] = [];
  const collectIds = (node: CategoryTreeNode) => {
    ids.push(node.id);
    if (node.children && node.children.length > 0) {
      node.children.forEach(collectIds);
    }
  };
  collectIds(category);
  return ids;
};

const getTotalDescendantCount = (category: CategoryTreeNode): number => {
  let count = 0;
  const countDescendants = (node: CategoryTreeNode) => {
    if (node.children && node.children.length > 0) {
      count += node.children.length;
      node.children.forEach(countDescendants);
    }
  };
  countDescendants(category);
  return count;
};

const checkAllDescendantsSelected = (category: CategoryTreeNode, selectedIds: string[]): boolean => {
  if (!category.children || category.children.length === 0) {
    return selectedIds.includes(category.id);
  }
  return (
    selectedIds.includes(category.id) &&
    category.children.every((child) => checkAllDescendantsSelected(child, selectedIds))
  );
};

const checkSomeDescendantsSelected = (category: CategoryTreeNode, selectedIds: string[]): boolean => {
  if (!category.children || category.children.length === 0) {
    return selectedIds.includes(category.id);
  }
  return (
    selectedIds.includes(category.id) ||
    category.children.some((child) => checkSomeDescendantsSelected(child, selectedIds))
  );
};

// Props for CategoryTreeItem
interface CategoryTreeItemProps {
  category: CategoryTreeNode;
  level: number;
  selectedCategoryIds: string[];
  expandedKeys: Set<string>;
  onToggle: (category: CategoryTreeNode) => void;
  onStandaloneToggle: (categoryId: string) => void;
  onExpandToggle: (categoryId: string) => void;
}

// Memoized CategoryTreeItem component defined outside the main component
const CategoryTreeItem = memo<CategoryTreeItemProps>(
  ({ category, level, selectedCategoryIds, expandedKeys, onToggle, onStandaloneToggle, onExpandToggle }) => {
    const hasChildren = category.children && category.children.length > 0;
    const isFullySelected = checkAllDescendantsSelected(category, selectedCategoryIds);
    const isIndeterminate = !isFullySelected && checkSomeDescendantsSelected(category, selectedCategoryIds);
    const descendantCount = getTotalDescendantCount(category);

    if (!hasChildren) {
      // Leaf node - simple checkbox
      const isSelected = selectedCategoryIds.includes(category.id);
      const isRootLevel = level === 0;
      return (
        <div
          className={`flex items-center gap-2 py-2 px-2 ${
            isRootLevel
              ? 'hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg'
              : 'hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded'
          } cursor-pointer ${getMarginClass(level)}`}
          onClick={() => onStandaloneToggle(category.id)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onStandaloneToggle(category.id);
            }
          }}
        >
          <Checkbox
            isSelected={isSelected}
            onValueChange={() => onStandaloneToggle(category.id)}
            size="sm"
            aria-label={category.name}
          />
          <span
            className={`text-sm ${
              isRootLevel ? 'text-gray-700 dark:text-gray-300' : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            {category.name}
          </span>
        </div>
      );
    }

    // Node with children - use nested accordion with controlled state
    const isExpanded = expandedKeys.has(category.id);

    return (
      <div className={getMarginClass(level)}>
        <Accordion
          selectionMode="multiple"
          className="px-0"
          selectedKeys={isExpanded ? [category.id] : []}
          onSelectionChange={() => onExpandToggle(category.id)}
          itemClasses={{
            base: 'py-0',
            title: 'font-normal text-sm',
            trigger: 'py-1.5 px-2 data-[hover=true]:bg-gray-50 dark:data-[hover=true]:bg-gray-800/50 rounded',
            content: 'pb-1',
          }}
        >
          <AccordionItem
            key={category.id}
            aria-label={category.name}
            classNames={{
              base: 'py-0',
            }}
            title={
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  isSelected={isFullySelected}
                  isIndeterminate={isIndeterminate}
                  onValueChange={() => onToggle(category)}
                  size="sm"
                  aria-label={`Select all in ${category.name}`}
                />
                <span className={`text-sm ${level === 0 ? 'font-medium' : ''} text-gray-700 dark:text-gray-300`}>
                  {category.name}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">({descendantCount})</span>
              </div>
            }
            indicator={
              isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-400 rotate-90" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-400" />
              )
            }
          >
            <div className="space-y-0.5">
              {category.children?.map((childCategory) => (
                <CategoryTreeItem
                  key={childCategory.id}
                  category={childCategory}
                  level={level + 1}
                  selectedCategoryIds={selectedCategoryIds}
                  expandedKeys={expandedKeys}
                  onToggle={onToggle}
                  onStandaloneToggle={onStandaloneToggle}
                  onExpandToggle={onExpandToggle}
                />
              ))}
            </div>
          </AccordionItem>
        </Accordion>
      </div>
    );
  },
);

CategoryTreeItem.displayName = 'CategoryTreeItem';

export interface NestedCategoryFilterProps {
  selectedCategoryIds: string[];
  onChange: (categoryIds: string[]) => void;
  className?: string;
}

const NestedCategoryFilter: React.FC<NestedCategoryFilterProps> = ({
  selectedCategoryIds,
  onChange,
  className = '',
}) => {
  const { t } = useTranslation('common');
  const { data: categoryTree = [], isLoading, error } = useCategoryTree();

  // Track expanded accordion items to prevent collapse on selection
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  // Handle category toggle (works for any level)
  const handleCategoryToggle = useCallback(
    (category: CategoryTreeNode) => {
      const allIds = getAllDescendantIds(category);
      const isCurrentlyFullySelected = checkAllDescendantsSelected(category, selectedCategoryIds);

      if (isCurrentlyFullySelected) {
        // Deselect all descendants
        onChange(selectedCategoryIds.filter((id) => !allIds.includes(id)));
      } else {
        // Select all descendants
        const newIds = new Set([...selectedCategoryIds, ...allIds]);
        onChange(Array.from(newIds));
      }
    },
    [selectedCategoryIds, onChange],
  );

  // Handle standalone category (no children) toggle
  const handleStandaloneToggle = useCallback(
    (categoryId: string) => {
      const isSelected = selectedCategoryIds.includes(categoryId);
      if (isSelected) {
        onChange(selectedCategoryIds.filter((id) => id !== categoryId));
      } else {
        onChange([...selectedCategoryIds, categoryId]);
      }
    },
    [selectedCategoryIds, onChange],
  );

  // Clear all selections
  const handleClearAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  // Handle accordion expand/collapse
  const handleExpandToggle = useCallback((categoryId: string) => {
    setExpandedKeys((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  }, []);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full rounded-lg" />
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-sm text-red-500 p-2">
          {t('errors.failedToLoadCategories', { defaultValue: 'Failed to load categories' })}
        </div>
      );
    }

    if (categoryTree.length === 0) {
      return (
        <div className="text-sm text-gray-500 dark:text-gray-400 p-2 text-center">
          {t('categories.empty', { defaultValue: 'No categories available' })}
        </div>
      );
    }

    return (
      <div className="space-y-1">
        {/* Render categories in order from backend (alphabetical) */}
        {categoryTree.map((category) => (
          <CategoryTreeItem
            key={category.id}
            category={category}
            level={0}
            selectedCategoryIds={selectedCategoryIds}
            expandedKeys={expandedKeys}
            onToggle={handleCategoryToggle}
            onStandaloneToggle={handleStandaloneToggle}
            onExpandToggle={handleExpandToggle}
          />
        ))}
      </div>
    );
  };

  return (
    <div className={`bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <FolderTree className="w-4 h-4 text-indigo-500" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {t('categories.title', { defaultValue: 'Categories' })}
          </h3>
        </div>
        {selectedCategoryIds.length > 0 && (
          <button
            onClick={handleClearAll}
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium"
          >
            {t('filters.clearAll', { defaultValue: 'Clear all' })}
          </button>
        )}
      </div>

      {/* Selected count */}
      {selectedCategoryIds.length > 0 && (
        <div className="px-4 py-2 bg-indigo-50 dark:bg-indigo-950/30 border-b border-gray-200 dark:border-gray-800">
          <span className="text-xs text-indigo-600 dark:text-indigo-400">
            {selectedCategoryIds.length} {t('filters.selected', { defaultValue: 'selected' })}
          </span>
        </div>
      )}

      {/* Category tree */}
      <div className="p-2 max-h-80 overflow-y-auto">{renderContent()}</div>
    </div>
  );
};

export default NestedCategoryFilter;
